import { useMemo } from 'react'

import NodeType from 'types/transformation/enums/NodeType'
import { isUnaryCondition } from 'types/transformation/enums/filter/Condition'
import FilterConditionType from 'types/transformation/enums/filter/Type'
import {
  AggregateNode,
  CalculateNode,
  CastNode,
  ClearNode,
  ConcatenateNode,
  DateParserNode,
  FilterNode,
  InputNode,
  LeftJoinNode,
  LowerNode,
  Node,
  ReplaceNode,
  TextReplaceNode,
  TimeDifferenceNode,
  TrimNode,
  UniteNode
} from 'types/transformation/types/nodes'

export function validateNode(node: Node['data']): boolean {
  switch (node.type) {
  case NodeType.AGGREGATE: {
    const validNode = node as AggregateNode

    return !!(
      validNode.left_node &&
        validNode.group_by &&
        (validNode.schema.length === 0 ||
          validNode.schema.every((col) => col.left_column && col.function))
    )
  }
  case NodeType.CALCULATE: {
    const validNode = node as CalculateNode

    // A stored expression holds bare column ids, so a surviving `@` is a
    // half-written reference that never resolved to a column.
    return !!(
      validNode.left_node &&
        validNode.expression &&
        !validNode.expression.includes('@') &&
        validNode.new_column
    )
  }
  case NodeType.CAST: {
    const validNode = node as CastNode

    return !!(
      validNode.left_node &&
        validNode.change_column &&
        validNode.change_column.id &&
        validNode.change_column.type
    )
  }
  case NodeType.CLEAR: {
    const validNode = node as ClearNode

    return !!(validNode.left_node && validNode.columns.length > 0)
  }
  case NodeType.FILTER: {
    const validNode = node as FilterNode

    return !!(
      validNode.left_node &&
        validNode.filter_conditions.length > 0 &&
        validNode.filter_conditions.every(
          (fc) =>
            fc.left_column &&
            fc.condition &&
            fc.operator &&
            (isUnaryCondition(fc.condition) ||
              fc.type === FilterConditionType.STANDARD_VALUE ||
              fc.right_column ||
              fc.right_value)
        )
    )
  }
  case NodeType.INPUT: {
    const validNode = node as InputNode

    return !!validNode.source_table_id
  }
  case NodeType.MAP: {
    // nothing to validate
    return true
  }
  case NodeType.LOWER: {
    const validNode = node as LowerNode

    return !!(validNode.left_node && validNode.left_column)
  }
  case NodeType.LEFT_JOIN: {
    const validNode = node as LeftJoinNode

    return !!(
      validNode.left_node &&
        validNode.left_column &&
        validNode.right_node &&
        validNode.right_column
    )
  }
  case NodeType.CONCATENATE: {
    const validNode = node as ConcatenateNode

    return !!(
      validNode.left_node &&
        validNode.columns.length > 0 &&
        validNode.columns.every((col) => col.left_column && col.right_column)
    )
  }
  case NodeType.DATE_PARSER: {
    const validNode = node as DateParserNode

    return !!(
      validNode.left_node &&
        validNode.left_column &&
        validNode.format &&
        validNode.new_column &&
        validNode.new_column.name
    )
  }
  case NodeType.TRIM: {
    const validNode = node as TrimNode

    return !!(validNode.left_node && validNode.left_column)
  }
  case NodeType.TEXT_REPLACE: {
    const validNode = node as TextReplaceNode

    return !!(
      validNode.left_node &&
        validNode.left_column &&
        validNode.search_text
    )
  }
  case NodeType.UNITE: {
    const validNode = node as UniteNode

    return !!(
      validNode.left_node &&
        validNode.right_node &&
        validNode.schema.length > 0 &&
        validNode.schema.every(
          (col) => (col.left_column || col.right_column) && col.name
        )
    )
  }
  case NodeType.REPLACE: {
    const validNode = node as ReplaceNode

    return !!(
      validNode.left_node &&
        validNode.left_column &&
        (validNode.replace_column || validNode.replace_value)
    )
  }
  case NodeType.TIME_DIFFERENCE: {
    const validNode = node as TimeDifferenceNode

    return !!(
      validNode.left_node &&
        validNode.left_column &&
        (validNode.right_column || validNode.value) &&
        validNode.unit &&
        validNode.new_column &&
        validNode.new_column.name
    )
  }
  // TODO: more nodes

  default:
    throw new Error(`Unsupported node type: ${node.type}`)
  }
}

export function useIsInvalid(node: Node['data']) {
  return useMemo(() => !validateNode(node), [node])
}

export function useGridIsInvalid(nodes: Node[]) {
  return useMemo(() => nodes.some((node) => !validateNode(node.data)), [nodes])
}
